import { Heading, PageHeader, Text } from "#app/components/text.tsx"
import { useState } from "react"
import Cta from "#app/components/cta.tsx"
import { Link } from "react-router"
import { buttonVariants } from "#app/components/ui/button.tsx"
import { Icon } from "#app/components/ui/icon.tsx"
import { Route } from "./+types"
import { SERVICE_AREAS_QUERY, ServiceAreasQuery } from "#app/queries/service-areas.ts"
import { BASE_URL, COMPANY_NAME, COMPANY_PHONE, LOCATION_CITY_STATE, SERVICE_COUNTIES, SERVICE_REGION } from "#app/utils/constants.ts"
import { getSeoMeta } from "#app/utils/seo/getSeoMeta.ts"
import { SEOHandle } from "@nasa-gcn/remix-seo"
import { serverOnly$ } from "vite-env-only/macros"
import { formatPhoneNumberForTelLink } from "#app/utils/misc.tsx"


const serverHandle: SEOHandle = {
  getSitemapEntries: async (request) => {
    // Dynamic import only on server
    const { getServiceAreaSitemapEntries } = await import('#app/utils/seo/sitemap-utils.server.ts');
    return getServiceAreaSitemapEntries(request);
  },
};

export const handle = serverOnly$(serverHandle);

export const meta: Route.MetaFunction = () => {
    const title = `Garage Door Service Areas | ${SERVICE_REGION} | ${COMPANY_NAME}`;
    const description = `${COMPANY_NAME} serves ${SERVICE_COUNTIES} from our base in ${LOCATION_CITY_STATE}. See our full service area list. Free estimates!`;
    const url = BASE_URL + '/service-areas';
    return getSeoMeta({
        title,
        description,
        url,
    })	
}

export const loader = async ({ request }: Route.LoaderArgs) => {
    const { client } = await import('#app/utils/sanity-client.ts');
    const query = SERVICE_AREAS_QUERY.query
    const params = {};
    const serviceAreaGroups = await client.fetch<ServiceAreasQuery>(query, params)

    if (!serviceAreaGroups) {
        throw new Response('Not found', { status: 404 })
    }
    return {
        serviceAreaGroups
    }
}


export default function ServiceAreasPage({loaderData}: Route.ComponentProps) {
    const {serviceAreaGroups} = loaderData
    const [hovered, setHovered] = useState<number | null>(null);
    const phoneLink = formatPhoneNumberForTelLink(COMPANY_PHONE);
    return (
        <main>
            <PageHeader variant="servicesPage" heading={`Garage Door Service Areas Across ${SERVICE_COUNTIES}`} className="bg-primary text-primary-foreground text-center mb-24 sm:mb-32">
                <Text size="lg">
                    {COMPANY_NAME}, proudly based in {LOCATION_CITY_STATE}, offers expert residential and commercial garage door services to communities across {SERVICE_COUNTIES}. We are committed to providing reliable installation, prompt 24/7 emergency repairs, and comprehensive maintenance throughout the region. Below is a list of the primary areas we serve. If you don't see your specific town listed but are nearby, please don't hesitate to contact us!
                </Text>
            </PageHeader>
            <div className="container mb-24 sm:mb-32 grid gap-6 grid-cols-1 sm:grid-cols-2">
                {serviceAreaGroups?.serviceAreas?.map((group, index) => (
                    <div key={index}>
                        {/* h2 to sit directly under the page h1 — the county groups are
                            the page's top-level sections. text-h4 keeps the original size. */}
                        <Heading as="h2" className="mb-6 text-h4">
                            {group.groupTitle}
                        </Heading>
                        <ul className="space-y-3">
                            {group.cities?.map((area) => (
                                <li key={area._id} className="flex items-start">
                                    <Icon name="map-pin" className="mr-2" />
                                    <Link prefetch="intent" to={`/service-areas/${area.slug?.current}`} className="text-primary hover:underline">
                                        {area.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
            <Cta heading="Need a Repair or Installation Quote?" backgroundStyle="accent">
                <Link to="/get-a-quote" className={buttonVariants({ size: "xl" })}>
                    Get a free quote
                    <Icon name="dollar-sign" />
                </Link>
                {phoneLink && (
                <Link to={phoneLink} className={buttonVariants({ variant: "white", size: "xl" })}>
                    {COMPANY_PHONE}
                    <Icon name="phone-outgoing" />
                </Link> 
                )}
            </Cta>
        </main>
    )
}